import { pacificToday } from '../../../../shared/dates';
import { summarizeLeadStages } from '../../logic/leadStages';
import type { IsoDate } from '../../../../shared/types';
import type { Snapshot } from '../../data/store';
import { inPersonOnly } from '../../logic/activity';
import { closeDates, deltaOf, ownerTalkRate, periodToDate, visitDay } from '../../logic/home';
import type { Delta, Period, PeriodCount } from '../../logic/home';
import { section, segmented } from '../../ui/components';
import { h, span } from '../../ui/dom';

const PERIODS: readonly { value: Period; label: string }[] = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
];

const pill = (label: string, tone: Delta['tone'], ariaLabel?: string): HTMLElement =>
  h('span', { class: `stat__pill stat__pill--${tone}`, 'aria-label': ariaLabel }, label);

const countTile = (label: string, p: PeriodCount): HTMLElement => {
  const delta = deltaOf(p);
  return h(
    'div',
    { class: 'stat' },
    span('stat__label', label),
    span('stat__value', String(p.current)),
    pill(delta.short, delta.tone, delta.text),
  );
};

/** The period's numbers, Square "Performance" style: big value + change vs the same stretch before. */
export const performanceCard = (
  snap: Snapshot,
  today: IsoDate,
  period: Period,
  onPeriod: (p: Period) => void,
): HTMLElement => {
  // the three numbers that say "visit" count in-person touches only (D90)
  const inPerson = inPersonOnly(snap.visits);
  const visits = periodToDate(inPerson.map(visitDay), today, period);
  const leads = periodToDate(
    snap.leads.map((l) => pacificToday(new Date(l.created_at))),
    today,
    period,
  );
  const closes = periodToDate(closeDates(snap.visits), today, period);
  const { current: { demo, proposal } } = summarizeLeadStages(snap.leads);
  const owner = ownerTalkRate(inPerson, today, period);
  return section(
    { title: 'Performance' },
    segmented({ options: PERIODS, value: period, onChange: onPeriod, size: 'md', columns: 2 }),
    h('p', { class: 'card__summary perf__vs' }, `Compared with ${visits.vs}`),
    h(
      'div',
      { class: 'kpigrid' },
      countTile('Visits', visits),
      countTile('New leads', leads),
      countTile('Closes', closes),
      h(
        'div',
        { class: 'stat' },
        span('stat__label', 'Current stage · Demo / Proposal'),
        span('stat__value', `${demo} · ${proposal}`),
        pill('Now', 'flat', 'Leads in demo and proposal right now'),
      ),
    ),
    owner === null ? null : h('p', { class: 'card__note' }, `Talked to the owner on ${owner}% of visits`),
  );
};
